import { isAxiosError } from "axios";
import { extractApiValidationMessages } from "@/utils/apiErrorMessages";
import type { FileErrorResponse } from "./types";
const reasons: Record<string, string> = {
  NUMBER_MATCHED: "番号で法人を特定",
  EXACT_NAME_AND_ADDRESS: "商号・所在地が完全一致",
  NORMALIZED_NAME_AND_ADDRESS: "商号・所在地が正規化後に一致",
  TOWN_MATCHED: "町名も一致",
  MULTIPLE_CANDIDATES: "複数候補から1法人を選択してください",
  NO_CANDIDATES: "候補がありません。番号を入力してください",
  TOO_MANY_CANDIDATES: "候補が多いため自動特定できません",
  NUMBER_NOT_IN_DATABASE: "国税庁データに未収録です",
  INVALID_CORPORATE_NUMBER: "法人番号は13桁で入力してください",
  INVALID_IDENTIFICATION_NUMBER: "会社法人等番号は12桁で入力してください",
  INVALID_CHECK_DIGIT: "法人番号の検査用数字が不正です",
  CONFLICTING_NUMBERS: "2つの番号が異なる法人を示しています",
  MISSING_REQUIRED_VALUES:
    "番号なしの場合は法人名・都道府県・市区町村が必要です",
  INVALID_MAPPED_CELL: "選択した列に数式などの読取エラーがあります",
  UNSUPPORTED_CORPORATION_KIND: "設立登記法人ではありません",
  CLOSED_CORPORATION: "閉鎖法人は取得対象外です",
  NON_LATEST_RECORD: "現在の法人情報ではありません",
  NORMALIZED_VALUE_TOO_LONG: "入力値が長すぎます",
  DUPLICATE_IDENTIFICATION_NUMBER: "同じ番号の行と重複しています",
  COMPANY_NAME_DIFFERS: "入力名称とDB名称が異なります。番号を優先しています",
  ADDRESS_DIFFERS: "入力所在地とDB所在地が異なります。番号を優先しています",
  HIDDEN_RECORD: "国税庁で検索対象除外とされた法人です。番号を確認してください",
};
export const reasonText = (code: string): string =>
  reasons[code] ?? "情報を確認してください";
export const statusText = {
  NUMBER_MATCHED: "番号一致",
  AUTO_ASSIGNED: "自動付与",
  NEEDS_REVIEW: "要確認",
  UNMATCHED: "未特定",
  ERROR: "エラー",
  DUPLICATE: "重複",
};
export const errorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const data = error.response?.data as
      | (Partial<FileErrorResponse> & {
          detail?: unknown;
          targets?: unknown[];
          rows?: unknown[];
        })
      | undefined;
    if (typeof data?.detail === "string") return data.detail;
    if (
      data &&
      Array.isArray(data.detail) &&
      data.detail.length > 0 &&
      data.detail.every((message) => typeof message === "string")
    )
      return data.detail.join(" ／ ");
    if (typeof data?.message === "string") {
      const location = [
        data.rowNumber != null ? `レコード${data.rowNumber}` : "",
        data.physicalLine != null ? `CSV ${data.physicalLine}行目` : "",
      ]
        .filter(Boolean)
        .join(" / ");
      return location ? `${location}: ${data.message}` : data.message;
    }
    const messages = extractApiValidationMessages(error);
    for (const rows of [data?.targets, data?.rows]) {
      if (!Array.isArray(rows)) continue;
      for (const row of rows) {
        if (row === null || typeof row !== "object") continue;
        const fields = row as Record<string, unknown>;
        if (typeof fields.detail === "string") {
          messages.push(
            typeof fields.rowNumber === "number" ||
              typeof fields.rowNumber === "string"
              ? `レコード${fields.rowNumber}: ${fields.detail}`
              : fields.detail
          );
        } else {
          messages.push(
            ...Object.values(fields).flatMap((value) =>
              Array.isArray(value)
                ? value.filter(
                    (item): item is string => typeof item === "string"
                  )
                : []
            )
          );
        }
      }
    }
    if (messages.length > 0) return messages.join(" ／ ");
    if (error.response?.status === 409)
      return "同じ受付キーで内容が変わっています。取得済み一覧と受付状況を確認してください。";
    if (error.response?.status === 400)
      return "入力内容を確認してください。ファイル形式・列対応・番号に不備があります。";
    if (error.response?.status === 403)
      return "この操作は現在の権限では利用できません。";
  }
  return "通信に失敗しました。時間をおいて再度お試しください。";
};
